import { useContext, useRef, useState, useEffect, useCallback } from 'react'
import { useSelector, shallowEqual, useDispatch } from 'react-redux'
import { createUseStyles } from 'react-jss'
import { useLocation } from 'react-router-dom'
import CreateJs from 'preload-js'
import { Context as GeneralContext } from '@/context'
import { Context as LoadingContext } from '@/context/loading'
import LocomotiveScroll from '@/components/LocomotiveScroll'
import Carousel from '@/components/Carousel'
import MainHero from '@/components/MainHero'
import DiagonalGallery from '@/components/DiagonalGallery'
import BtnLink from '@/components/BtnLink'
import YoutubeVideo from '@/components/YoutubeVideo'
import Meta from '@/components/Meta'
import Footer from '@/components/Footer'
import useHandleAllMediaWithCb from '@/hooks/useHandleAllMediaWithCb'
import { externalPreload } from '@/utils/dom'
import actions from '@/actions'
import utils from '@/utils'
import { getSlug } from '@/utils/path'
import style from './style'

const useStyles = createUseStyles(style)

const PageBuilder = () => {
  const classes = useStyles()
  const { getImageUrl } = useContext(GeneralContext)
  const { setReady, ready } = useContext(LoadingContext)
  const preload = useRef(new CreateJs.LoadQueue())
  const location = useLocation()
  const stub = useRef(getSlug(location.pathname))
  const [load, setLoad] = useState(false)
  const $root = useRef()

  /*------------------------------
  Hooks to handle media loading & updating the scrollbar
  ------------------------------*/
  useHandleAllMediaWithCb({
    init: load,
    ref: $root.current?.ref,
  })

  /*------------------------------
  Redux Connect
  ------------------------------*/
  const { current, page, mediaQueryKey, strings } = useSelector((state) => ({
    current: state.page.current,
    page: state.page.pages[stub.current] || {},
    mediaQueryKey: state.mediaQuery.key,
    strings: state.language.string,
  }), shallowEqual)

  /*------------------------------
  Redux Actions
  ------------------------------*/
  const dispatch = useDispatch()
  const getPage = useCallback((index) => dispatch(actions.page.getDataPage(index)), [dispatch])
  const setCurrentPage = useCallback((index) => dispatch(actions.page.setCurrent(index)), [dispatch])

  /*------------------------------
  Complete Preload
  ------------------------------*/
  const completePreload = () => {
    externalPreload(setLoad)
    setReady(true)
  }

  /*------------------------------
  Preload Media
  ------------------------------*/
  const preloadMedia = () => {
    preload.current.on('complete', completePreload)
    const heroMedia = getImageUrl(page.featured_media, 'full', stub.current, 'pages')
    preload.current.loadManifest(heroMedia)
  }

  /*------------------------------
  Set Gallery
  ------------------------------*/
  const setGallery = (imagesList) => {
    if (imagesList.length > 0) {
      const gal = imagesList.split(',')
      const imgSize = mediaQueryKey > 2 ? 'full' : 'large'
      const galleryItems = gal.map((i) => {
        // Controllo se esiste l'immagine su media_post
        if (page.media_post[i]) {
          const { width, height, caption } = page.media_post[i]
          const src = getImageUrl(i, imgSize, stub.current, 'pages')
          const ratio = height / width

          return {
            src,
            width,
            height,
            caption,
            ratio,
          }
        }
        return null
      })

      return galleryItems[0] ? galleryItems : []
    }
    return []
  }

  /*------------------------------
  Preload Media when there are Data of Page in Redux
  ------------------------------*/
  useEffect(() => {
    if (Object.keys(page).length > 0) {
      preloadMedia()
    }
  }, [page])

  /*------------------------------
  Initialize
  ------------------------------*/
  const initialize = () => {
    if (current !== stub.current) setCurrentPage(stub.current, 'pages')
    getPage(stub.current)
  }

  /*------------------------------
  Did Mount
  ------------------------------*/
  useEffect(() => {
    if (!load) initialize()
  }, [])

  /*------------------------------
  Render Helmet
  ------------------------------*/
  const renderHelmet = () => {
    return load && (
      <Meta
        title={page.yoast_meta.find((y) => y.property?.includes('title')).content || page.title.rendered}
        meta={page.yoast_meta}
        schema={page.yoast_json_ld}
      />
    )
  }

  /*------------------------------
  Render Cover People
  ------------------------------*/
  const renderCoverProject = () => {
    return (page.title.rendered && page.featured_media) && (
      <MainHero
        className={classes.hero}
        title={utils.strings.splitAfterFirstSpace(page.title.rendered)}
        img={getImageUrl(page.featured_media, 'full', stub.current, 'pages')}
        ready={ready}
        scrollIcon
        parallax
        delay={1}
        layout="bottom"
        plainFont
      />
    )
  }

  /*------------------------------
  Render Big Text
  ------------------------------*/
  const renderBigText = (el, index) => {
    return el && (
      <div
        key={index}
        className={`${classes.wrapper} ${classes.introText}`}
        dangerouslySetInnerHTML={{ __html: el }}
      />
    )
  }

  /*------------------------------
  Render Diagonal Gallery
  ------------------------------*/
  const renderDiagonalGallery = (el, index) => {
    const gallery = setGallery(el.element_gallery)
    return (
      <div
        key={index}
        className={classes.wrapper}
      >
        <DiagonalGallery items={gallery} />
      </div>
    )
  }

  /*------------------------------
  Render Words
  ------------------------------*/
  const renderWords = (el, index) => {
    return (
      <div
        key={index}
        className={classes.wrapper}
      >
        <div className={classes.grid}>
          <div className={classes.gridHeading}>
            <h2>{strings.words_title}</h2>
          </div>
          <div className={classes.gridContent}>
            <div dangerouslySetInnerHTML={{ __html: el.element_text }} />
            {el.element_link && (
              <BtnLink
                link={el.element_link}
                label={el.element_label}
              />
            )}
          </div>
        </div>
      </div>
    )
  }

  /*------------------------------
  Render Video
  ------------------------------*/
  const renderVideo = (el, i) => {
    return (
      <div
        key={i}
        className={classes.wrapper}
      >
        <YoutubeVideo
          className={classes.video}
          videoId={el.element_video}
        />
      </div>
    )
  }

  /*------------------------------
  Render Simple Gallery
  ------------------------------*/
  const renderSimpleGallery = (el, index) => {
    const gallery = setGallery(el.element_gallery)
    return (
      <Carousel
        key={index}
        imagesList={gallery}
      />
    )
  }

  const renderDownloadBtn = () => {
    return page.download_link_url && (
      <div className={`${classes.wrapper} ${classes.download}`}>
        <BtnLink
          link={page.download_link_url}
          label={page.download_link_label}
          icon="download"
        />
      </div>
    )
  }

  /*------------------------------
  Render Page Elements
  ------------------------------*/
  const renderPageElements = (pageElements) => {
    return pageElements && pageElements.map((element, i) => {
      if (element.layout_choice === 'diagonal_gallery') return renderDiagonalGallery(element, i)
      if (element.layout_choice === 'simple_gallery') return renderSimpleGallery(element, i)
      if (element.layout_choice === 'words') return renderWords(element, i)
      if (element.layout_choice === 'article') return renderBigText(element.element_text, i)
      if (element.layout_choice === 'video') return renderVideo(element, i)
      return null
    })
  }

  /*------------------------------
  Render Footer
  ------------------------------*/
  const renderFooter = () => <Footer />

  /*------------------------------
  Render Content
  ------------------------------*/
  const renderContent = () => {
    return load && (
      <>
        {renderCoverProject()}
        {renderBigText(page.content.rendered, 99)}
        {renderPageElements(page.page_element)}
        {renderDownloadBtn()}
        {renderFooter()}
      </>
    )
  }

  return (
    <LocomotiveScroll
      init={load}
      className={`page pagePageBuilder ${classes.root}`}
      ref={$root}
    >
      {renderHelmet()}
      {renderContent()}
    </LocomotiveScroll>
  )
}

export default PageBuilder
